<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { TableHeaderField, TableDataField, type ITableField } from "../../components/Table";
  import MainLayout from "../../layout/MainLayout.svelte";
  import { visitDtoFields } from "../../utils/TableFields/visit-dto.fields";
  import {
    loading,
    records,
    initList,
    markItemViewed,
    startEditingNote,
    editingNote,
    saveNote,
    markItemLocked,
    markItemUnviewed,
    markItemUnprinted,
    onDateFilterChanged,
    filter,
    load,
  } from "./visit.store";
  import Button from "../../components/Button.svelte";
  import SearchHeader from "../../components/SearchHeader.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import { isUserRoleADM } from "../../app.store";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import Label from "../../components/Form/Label.svelte";
  import Icon from "../../components/Icon.svelte";
  import DateInputString from "../../components/Form/Fields/DateInputString.svelte";

  const REFRESH_SEC = 30;

  let colspanValue = visitDtoFields.length + 1;
  let visibleFields = visitDtoFields;
  let refreshSub: number | null = null;

  function renderField(field: ITableField<any>, record: any) {
    if (field.render != null && typeof field.render === "function") {
      return field.render(record);
    }
    return record[field.name];
  }

  function startRefresh() {
    refreshSub = setInterval(() => {
      initList();
    }, REFRESH_SEC * 1000);
  }

  function stopRefresh() {
    if (!!refreshSub) {
      clearInterval(refreshSub);
    }
  }

  // auto-refresh every 30 seconds (DISABLED)
  // and LIMIT results to current date
  onMount(() => {
    initList();
    // NO AUTOMATIC REFRESH
    //startRefresh();
  });
  onDestroy(() => stopRefresh());
</script>

<MainLayout>
  <SearchHeader entitySingular="capo" entityPlural="capi" searchFieldVisible={false} numRecords={$records != null ? $records.length : 0}>
    <div slot="search" class="level-item weight-label-search-box">
      {#if $isUserRoleADM}
        <div style="min-width: 5rem;">
          <DateInputString name="" placeholder="Data Macellazione" required={false} value={$filter.criteria.refDate} on:select={onDateFilterChanged} />
        </div>
      {/if}
      <p class="control" style="margin-left:1rem;">
        <Button klass="is-warning" on:click={load}>
          <span>Aggiorna</span>
        </Button>
      </p>
      <div style="min-width: 5rem;margin-left:3rem;">
        <Label>Mostra completati</Label>
      </div>
      <div style="min-width: 5rem;margin-left:1rem;">
        <Switch name="" bind:value={$filter.criteria.showCompleted} on:toggle={load} />
      </div>
    </div>
  </SearchHeader>

  <table class="table is-striped is-fullwidth table-base">
    <thead>
      <tr>
        {#each visibleFields as field}
          <TableHeaderField klass={field.titleClass || ""} sortable={field.sortable}>
            {field.title}
          </TableHeaderField>
        {/each}
        <TableHeaderField klass="table-buttons"></TableHeaderField>
      </tr>
    </thead>

    <tbody>
      {#if $loading == false}
        {#if $records.length == 0}
          <tr class="table-no-data">
            <td colspan={colspanValue}> nessun elemento da visualizzare </td>
          </tr>
        {:else}
          {#each $records as record}
            <tr
              class:visit-item-worked={record.worked == 1 && record.viewed == 0}
              class:visit-item-viewed={record.worked == 1 && record.viewed == 1 && record.locked == 0 && record.passed == 1}
              class:visit-item-locked={record.worked == 1 && record.viewed == 1 && record.locked == 1}
              class:visit-item-discarded={record.worked == 1 && record.viewed == 1 && record.locked == 0 && record.passed == 0}
              class:visit-item-skipped={record.skipped == 1}
            >
              {#each visibleFields as field}
                <TableDataField klass={field.dataClass || ""}>
                  {@html renderField(field, record)}
                </TableDataField>
              {/each}
              <TableDataField klass="has-text-centered">
                {#if $isUserRoleADM}
                  {#if record.viewed == 1}
                    <Button klass="is-danger" style="width:10rem;" on:click={(_) => markItemUnviewed(record)}>Resetta</Button>
                  {:else}
                    <Button klass="is-light" style="width:10rem;" disabled={record.worked == 0 || record.viewed == 1} on:click={(_) => markItemViewed(record)}>
                      Visto
                    </Button>
                    <Button klass="is-danger" style="width:10rem;" disabled={record.worked == 0 || record.viewed == 1} on:click={(_) => markItemLocked(record)}>
                      Vincola
                    </Button>
                  {/if}
                {:else}
                  <Button klass="is-light" style="width:10rem;" disabled={record.worked == 0 || record.viewed == 1} on:click={(_) => markItemViewed(record)}>
                    Visto
                  </Button>
                  <Button klass="is-danger" style="width:10rem;" disabled={record.worked == 0 || record.viewed == 1} on:click={(_) => markItemLocked(record)}>
                    Vincola
                  </Button>
                {/if}
              </TableDataField>
            </tr>
            <tr>
              <td colspan={colspanValue - 1}>
                {#if $editingNote === record.slaughterItemOid}
                  <Input name="" placeholder="inserire le note per questo capo" bind:value={record.vetNotes} />
                {:else}
                  <span class="visit-item-notes-label">Note:</span>&nbsp;{record.vetNotes}
                {/if}
              </td>
              <TableDataField klass="has-text-centered">
                {#if $editingNote === record.slaughterItemOid}
                  <Button klass="is-light" style="width:10rem;" on:click={(_) => saveNote(record)}>Salva note</Button>
                {:else}
                  <Button klass="is-light" style="width:4rem;" disabled={record.worked == 0} altText="Modifica Note" on:click={(_) => startEditingNote(record)}>
                    <Icon name="edit" />
                  </Button>
                  {#if $isUserRoleADM && record.worked == 1 && record.printed == 1}
                    <Button klass="is-light" style="width:4rem;" altText="Ristampa Etichetta" on:click={(_) => markItemUnprinted(record)}>
                      <Icon name="printer" />
                    </Button>
                  {/if}
                {/if}
              </TableDataField>
            </tr>
          {/each}
        {/if}
      {:else}
        <tr class="table-no-data">
          <td colspan={colspanValue}>
            <div class="table-loader" />
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</MainLayout>

<style>
  .table-base {
    margin-top: 1.5rem;
  }

  .table-base tr.table-no-data {
    transition: all 1s linear;
  }

  tr.visit-item-worked {
    background-color: rgba(255, 217, 0, 0.74) !important;
  }
  tr.visit-item-viewed {
    background-color: rgba(0, 166, 0, 0.74) !important;
  }
  tr.visit-item-locked {
    background-color: rgba(200, 0, 200, 0.74) !important;
  }
  tr.visit-item-skipped {
    background-color: rgba(200, 30, 30, 0.74) !important;
  }
  tr.visit-item-discarded {
    background-color: rgba(200, 30, 30, 0.74) !important;
  }

  .visit-item-notes-label {
    font-weight: bold;
  }
</style>
